import { FC, FormEvent, useEffect, useState } from 'react'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { toast } from 'react-toastify'
import { ReactComponent as PencilIcon } from 'src/assets/svg/Pancile.svg'
import {
  CALLING_CODE_BY_COUNTRY,
  CALLING_CODE_OPTIONS,
} from 'src/constants/callingCodes'
import { COUNTRY_OPTIONS } from 'src/constants/countries'
import en from 'src/constants/en'
import { MINIMUM_PASSWORD_LENGTH } from 'src/constants/password'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import { GetSelfUser } from 'src/services/napi/getSelf/interfaces/getSelf.interface'
import { UpdateSelfParams } from 'src/services/napi/updateSelf/interfaces/updateSelf.interface'
import { UpdateSelfPasswordParams } from 'src/services/napi/updateSelfPassword/interfaces/updateSelfPassword.interface'
import { updateUserProfile } from 'src/services/userFeaturesApi'
import styles from './personalInformation.module.scss'
import ChangePasswordModal from '../ChangePasswordModal'

// Georgia (+995) — preselected calling code when the user has no saved phone.
const DEFAULT_PHONE_COUNTRY = 'GE'

interface PersonalInformationProps {
  handleUpdatePassword: (params: UpdateSelfPasswordParams) => Promise<boolean>
  handleUpdateUser: (
    params: UpdateSelfParams,
    successMessage?: string
  ) => Promise<boolean>
  onSavingChange?: (saving: boolean) => void
  userData?: GetSelfUser
}

const PersonalInformation: FC<PersonalInformationProps> = ({
  userData,
  handleUpdateUser,
  handleUpdatePassword,
  onSavingChange,
}) => {
  const { data: features, refetch: refetchFeatures } = useGetUserFeaturesQuery()
  const savedCountry = features?.data?.country || ''

  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [company, setCompany] = useState<string>('')
  const [phoneCountry, setPhoneCountry] = useState<string>(
    DEFAULT_PHONE_COUNTRY
  )
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [country, setCountry] = useState<string>('')
  const [oldPassword, setOldPassword] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false)

  // Build an E.164-ish string (e.g. +995555123456) from the selected country
  // code and the typed national number; empty when either part is missing.
  const composePhone = (countryIso: string, number: string): string => {
    const digits = number.replace(/\D/g, '')
    const code = CALLING_CODE_BY_COUNTRY.get(countryIso)

    return digits && code ? `+${code}${digits}` : ''
  }

  const composedPhone = composePhone(phoneCountry, phoneNumber)

  const resetPasswordFields = () => {
    setOldPassword('')
    setNewPassword('')
    setConfirmPassword('')
  }

  const handlePasswordSave = async (
    currentPassword: string,
    newPass: string
  ) => {
    const success = await handleUpdatePassword({
      newPassword: newPass,
      oldPassword: currentPassword,
    })

    if (success) {
      setShowPasswordModal(false)
      resetPasswordFields()
      toast.success('Password updated successfully')
    }
  }

  const setDataFromUser = () => {
    setFirstName(userData?.firstName || '')
    setLastName(userData?.lastName || '')
    setEmail(userData?.email || '')
    setCompany(userData?.metadata?.companyName || '')

    const parsedPhone = userData?.phone
      ? parsePhoneNumberFromString(userData.phone)
      : undefined
    setPhoneCountry(parsedPhone?.country || DEFAULT_PHONE_COUNTRY)
    setPhoneNumber(parsedPhone?.nationalNumber || '')

    setCountry(savedCountry)

    resetPasswordFields()
  }

  const hasPhoneChanged = (): boolean =>
    composedPhone !== (userData?.phone || '')

  const hasOtherUserDataChanged = (): boolean =>
    email !== userData?.email ||
    firstName !== userData?.firstName ||
    lastName !== userData?.lastName ||
    company !== userData?.metadata?.companyName

  const hasUserDataChanged = (): boolean =>
    hasOtherUserDataChanged() || hasPhoneChanged()

  const hasCountryChanged = (): boolean => country !== savedCountry

  const hasEnteredNewPassword = (): boolean => {
    return !!newPassword || !!confirmPassword
  }

  const arePasswordsValid = (): boolean => {
    if (!newPassword && !confirmPassword) {
      return false
    }

    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error(en.PASSWORD_ERROR_NOT_FILLED_ALL_FIELDS)
      return false
    }

    if (newPassword !== confirmPassword) {
      toast.error(en.PASSWORD_ERROR_NOT_MATCHING)
      return false
    }

    if (newPassword.length < MINIMUM_PASSWORD_LENGTH) {
      toast.error(en.PASSWORD_ERROR_TOO_SHORT)
      return false
    }

    return true
  }

  const handleReset = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setDataFromUser()
  }

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault()

    if (hasEnteredNewPassword() && !arePasswordsValid()) {
      return
    }

    onSavingChange?.(true)

    try {
      if (hasEnteredNewPassword()) {
        const success = await handleUpdatePassword({ newPassword, oldPassword })

        if (!success) {
          return
        }
      }

      if (hasUserDataChanged()) {
        // When only the phone changed, surface a phone-specific message;
        // otherwise fall back to the generic user-updated message.
        const successMessage = hasOtherUserDataChanged()
          ? en.USER_UPDATE_SUCCESS_MESSAGE
          : 'Mobile phone updated successfully'

        const success = await handleUpdateUser(
          {
            email,
            firstName,
            lastName,
            metadata: {
              companyName: company,
            },
            phone: composedPhone,
          },
          successMessage
        )

        if (!success) {
          return
        }
      }

      if (hasCountryChanged()) {
        try {
          await updateUserProfile({ country })
          await refetchFeatures()
          toast.success('Country updated successfully')
        } catch (error) {
          toast.error('Failed to update country')
          return
        }
      }

      resetPasswordFields()
    } finally {
      onSavingChange?.(false)
    }
  }

  useEffect(() => {
    setDataFromUser()
  }, [userData])

  // Country lives in user-features, which loads independently of userData.
  useEffect(() => {
    setCountry(savedCountry)
  }, [savedCountry])

  return (
    <>
      <form
        className={styles.personalInformation}
        id="personalInformationForm"
        onReset={handleReset}
        onSubmit={handleSubmit}
      >
        <h3 className={styles.title}>Personal Information</h3>

        <div className={styles.formGrid}>
          <div className={styles.formField}>
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              onChange={(e) => setFirstName(e.target.value)}
              type="text"
              value={firstName}
            />
          </div>

          <div className={styles.formField}>
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              onChange={(e) => setLastName(e.target.value)}
              type="text"
              value={lastName}
            />
          </div>

          <div className={styles.formField}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              value={email}
            />
          </div>

          <div className={styles.formField}>
            <label htmlFor="company">Company</label>
            <input
              id="company"
              onChange={(e) => setCompany(e.target.value)}
              type="text"
              value={company}
            />
          </div>

          <div className={styles.formField}>
            <label htmlFor="password">Password</label>
            <div className={styles.passwordField}>
              <input id="password" readOnly type="password" value="********" />
              <button
                className={styles.editPasswordBtn}
                onClick={() => setShowPasswordModal(true)}
                type="button"
              >
                <PencilIcon />
              </button>
            </div>
          </div>

          <div className={styles.formField}>
            <label htmlFor="mobilePhone">Mobile Phone</label>
            <div className={styles.phoneRow}>
              <select
                aria-label="Country code"
                onChange={(e) => setPhoneCountry(e.target.value)}
                value={phoneCountry}
              >
                <option value="">Code</option>
                {CALLING_CODE_OPTIONS.map((option) => (
                  <option key={option.country} value={option.country}>
                    {option.name} (+{option.callingCode})
                  </option>
                ))}
              </select>
              <input
                id="mobilePhone"
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter mobile phone"
                type="tel"
                value={phoneNumber}
              />
            </div>
          </div>

          <div className={styles.formField}>
            <label htmlFor="country">Country</label>
            <select
              id="country"
              onChange={(e) => setCountry(e.target.value)}
              value={country}
            >
              <option value="">Select country</option>
              {COUNTRY_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </form>

      {showPasswordModal && (
        <ChangePasswordModal
          onClose={() => setShowPasswordModal(false)}
          onSave={handlePasswordSave}
        />
      )}
    </>
  )
}

export default PersonalInformation
